import store from "../app/store";

/**
 * Fetches all bots associated with the currently authenticated user.
 *
 * - Sends a GET request to `/user/getUserBot`.
 * - Requires a valid JWT token from Redux store.
 * - Returns an array of bot objects or an error message.
 *
 * @async
 * @function GetUserBots
 * @returns {Promise<{
 *   data: Array<Object> | null,
 *   error: boolean | string
 * }>} A promise resolving to:
 * - `data`: Array of user bot objects if successful, or `null` on failure.
 * - `error`: `false` if successful, or `true`/string with error detail if failed.
 *
 * @example
 * const result = await GetUserBots();
 * if (!result.error) {
 *   console.log("User bots:", result.data);
 * } else {
 *   console.error("Failed to fetch bots:", result.error);
 * }
 */

const GetUserBots = async () => {
  const domain = process.env.REACT_APP_SERVER;
  const url = `${domain}/user/getUserBot`;

  const token = store.getState().auth.token;

  if (!token) {
    return { data: null, error: "No token found" };
  }

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const res = await fetch(url, options);

    if (!res.ok) {
      throw new Error(`Error fetching bots: ${res.status} ${res.statusText}`);
    }

    const data = await res.json();

    return { data: data?.data, error: false };
  } catch (error) {
    return { data: null, error: true };
  }
};

export default GetUserBots;
